import {html, TemplateResult} from 'lit';
import {unsafeHTML} from 'lit/directives/unsafe-html.js';
import {classMap} from "lit/directives/class-map.js";

export interface PaginationInterface {
    page: number;
    pageHref: string;
    totalPages: number;
}

export class Pagination {
    static readonly PREVIOUS_LABEL = 'Previous';
    static readonly NEXT_LABEL = 'Next';
    static readonly MAX_PAGES_TO_SHOW = 8;
    static readonly DOTS_ELEMENT = html`
        <sl-button size="medium" disabled>...</sl-button>`;

    constructor(props?: PaginationInterface, filters?: string) {
        this._page = props?.page || 0;
        this._pageHref = props?.pageHref || '';
        this._totalPages = props?.totalPages || 0;
        this._showPagination = this._page > 0 && this._totalPages > 0;
        this._filters = filters || '';
    }

    private _showPagination: boolean;
    private _page: number;
    private _pageHref: string;
    private _totalPages: number;
    private _filters: string;

    private renderRangePageNumbers = (start: number, end: number, currentPage?: number) => {
        let pageButtons: TemplateResult[] = [];

        let pg = start < 1 ? 1 : start;
        let max = end > this._totalPages ? this._totalPages : end;

        for (pg; pg <= max; pg++) {
            let pageDisabled = '';
            var pageClass = {
                "bg-document-list-page": true,
                "active": (currentPage === pg)
            }
            if (currentPage === pg) {
                pageDisabled = ' disabled';
            }

            pageButtons.push(html`
                <sl-button size="medium" class="${classMap(pageClass)}" ${pageDisabled} page='${pg}'>${pg}
                </sl-button>`
            );
        }
        return pageButtons;
    }

    private renderCurrentPageNumberAndSurround = (currentPage: number, howManyEitherSide: number, showDotsBefore: boolean, showDotsAfter: boolean) => {
        if (showDotsBefore && showDotsAfter) {
            return this.renderRangePageNumbers(currentPage - howManyEitherSide, currentPage + howManyEitherSide, currentPage);
        } else if (showDotsBefore) {
            return this.renderRangePageNumbers(currentPage - howManyEitherSide, currentPage, currentPage);
        } else if (showDotsAfter) {
            return this.renderRangePageNumbers(currentPage, currentPage + howManyEitherSide, currentPage);
        } else {
            return this.renderRangePageNumbers(currentPage, currentPage, currentPage);
        }
    }

    renderPagination = () => {
        if (!this._showPagination) {
            return '';
        }

        // pagination starts at 1
        const currentPage = this._page;
        const totalPages = this._totalPages;
        const showDots = totalPages > Pagination.MAX_PAGES_TO_SHOW;
        const showDotsBefore = (showDots && (currentPage - Math.round(Pagination.MAX_PAGES_TO_SHOW / 2) > 1));
        const showDotsAfter = (showDots && (currentPage + Math.round(Pagination.MAX_PAGES_TO_SHOW / 2)) < totalPages);

        const numbersBefore = showDotsBefore ? Pagination.DOTS_ELEMENT :
            this.renderRangePageNumbers(1, currentPage - 1);
        const numbersAfter = showDotsAfter ? Pagination.DOTS_ELEMENT :
            this.renderRangePageNumbers(currentPage + 1, totalPages);
        const mainNumbers = this.renderCurrentPageNumberAndSurround(currentPage, Math.round(Pagination.MAX_PAGES_TO_SHOW / 2), showDotsBefore, showDotsAfter)

        const previousLink = currentPage > 1 ? `${this._pageHref}${currentPage - 1}${this._filters}` : '';
        const disabledPreviousLink = !previousLink ? ` disabled` : '';
        const previous = unsafeHTML(`<sl-button size="medium" class="bg-button bg-button-secondary bg-pagination-previous bg-document-list-page" ${disabledPreviousLink} page='previous'>${Pagination.PREVIOUS_LABEL}</sl-button>`);
        const nextLink = currentPage < totalPages ? `${this._pageHref}${currentPage + 1}${this._filters}` : '';
        const disabledNextLink = !nextLink ? ` disabled` : '';
        const next = unsafeHTML(`<sl-button size="medium" class="bg-button bg-pagination-next bg-document-list-page" ${disabledNextLink} page='next'>${Pagination.NEXT_LABEL}</sl-button>`);

        return html`
            <div class="bg-pagination">
                <sl-button-group class="bg-pagination-container" label="Pagination">
                    ${previous}
                    ${numbersBefore}
                    ${mainNumbers}
                    ${numbersAfter}
                    ${next}
                </sl-button-group>
            </div>`;
    }
}
